import type { User } from 'firebase/auth';

/**
 * The `localStorage["user"]` record the email-login path writes (KB-3033).
 *
 * WHY this is a shared function rather than an inline object literal: it now has
 * TWO writers. `LoginPage.handleLogin` writes it on the normal success path, and
 * `AuthContext.login` writes it when a bounded sign-in settles successfully
 * AFTER its timeout already fired (round-1 review finding #1). If those two
 * drifted, the reconciled login would render a differently-shaped record than
 * the ordinary one and the bug would come back wearing a different hat.
 *
 * `DashboardLayout.tsx:29` is the reader — `JSON.parse(localStorage.getItem("user"))`
 * for the sidebar's name and email. It has no fallback for a missing key, which
 * is precisely why a login that succeeds without this write shows a blank shell.
 *
 * The field shapes (including `role`/`organizationRole` being hardcoded
 * defaults, and `lastName` being `undefined` for a single-word display name)
 * are preserved EXACTLY as the pre-existing success path wrote them. This
 * ticket is not the place to change what the record contains; it is only the
 * place to stop it going unwritten.
 */
export function persistLoginUserRecord(user: User): void {
  localStorage.setItem(
    'user',
    JSON.stringify({
      id: user.uid,
      email: user.email,
      role: 'camp_user', // Default role - in a real app this would come from your database
      organizationRole: 'Organization owner', // Default role - in a real app this would come from your database
      firstName: user.displayName ? user.displayName.split(' ')[0] : 'User',
      lastName: user.displayName ? user.displayName.split(' ')[1] : '',
    }),
  );
}
